import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { sanitizeErrorForRum } from 'reportDataSignedUrl';

import { logger } from '../logger';

type Namespace =
  | 'auth'
  | 'broadcast'
  | 'backstage'
  | 'captions'
  | 'devices'
  | 'natalie'
  | 'nattering'
  | 'event_wizard'
  | 'forms'
  | 'media_settings'
  | 'user'
  | 'video_service'
  | 'tech_check'
  | 'open_natter'
  | 'event'
  | 'reporting';

type Action = `${Namespace}.${string}` | `${Namespace}.${string}.${string}`;

/**
 * Tracker interface for tracking user interactions and setting user context.
 * Sets user context for both RUM and Logs.
 *
 * @see {@link https://docs.datadoghq.com/real_user_monitoring/guide/send-rum-custom-actions} for more information.
 */
export const monitoring = {
  /**
   * Telemetry never decides whether the work succeeded.
   *
   * These calls sit inside the `try` that reports a failure — a create, a publication — so a throw
   * from here would be caught and reported as if the operation itself had failed. It has happened
   * in review: a created event reported as a failed create, with the organiser sent back to
   * resubmit something that already existed. RUM usually no-ops when it is not initialised, but
   * "usually" is the wrong guarantee for a line that cannot affect the outcome either way.
   *
   * Swallowed at the boundary rather than at each call site, so a caller cannot forget.
   */
  trackAction: (action: Action, extra?: Record<string, unknown>) => {
    try {
      if (extra) {
        logger.info(`[monitoring.trackAction] ${action}`, extra);
        datadogRum.addAction(action, extra);
      } else {
        logger.info(`[monitoring.trackAction] ${action}`);
        datadogRum.addAction(action);
      }
    } catch (error) {
      // Not `logger.error`: this IS the reporting path, and a failure to report is not the
      // application's problem to escalate. A warning leaves a trace without inventing an incident.
      logger.warn(`[monitoring.trackAction] could not record "${action}"`, { error });
    }
  },
  /**
   * Emit a structured event to the Datadog Logs index only — no RUM action. Attributes are written
   * flat at the log's top level (not nested under `ctx` like the shared logger) so they read as clean
   * facets for a log-based metric (Generate Metrics from Logs). Use this when the event's primary
   * consumer is a metric and a RUM action would be redundant; use {@link trackAction} when the
   * interaction should also appear on the RUM session timeline.
   */
  logEvent: (event: string, attributes?: Record<string, unknown>) => {
    datadogLogs.logger.info(event, attributes);
  },
  setUser: (user: Parameters<typeof datadogRum.setUser>[0]) => {
    datadogRum.setUser(user);
    datadogLogs.setUser(user);
  },
  setUserProperty: (key: string, value: unknown) => {
    datadogRum.setUserProperty(key, value);
    datadogLogs.setUserProperty(key, value);
  },
  clearUser: () => {
    datadogRum.clearUser();
    datadogLogs.clearUser();
  },
  setContext: (key: string, value: unknown) => {
    datadogRum.setGlobalContextProperty(key, value);
    datadogLogs.setGlobalContextProperty(key, value);
  },
  removeContext: (key: string) => {
    datadogRum.removeGlobalContextProperty(key);
    datadogLogs.removeGlobalContextProperty(key);
  },
  /**
   * Reports an error to Datadog RUM for tracking and alerting.
   *
   * RUM cannot edit an error's causes before sending, so signed report links and email addresses come
   * out of a copy here. Like {@link trackAction}, a failure to report never throws into the caller.
   * @param error - The error to report
   * @param context - Additional context to attach to the error
   */
  reportError: (error: unknown, context?: Record<string, unknown>) => {
    try {
      datadogRum.addError(error instanceof Error ? sanitizeErrorForRum(error) : error, context);
    } catch (reportFailure) {
      // The Logs SDK keeps only an object's enumerable fields, and an Error's message and stack are not.
      logger.warn('[monitoring.reportError] could not report an error', { error: String(reportFailure) });
    }
  },
};
